import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Eye, EyeOff, Copy } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

const LIST_PATH = "/liveness/admin/api-tokens";

function RotateConfirmModal({ onClose, onConfirm, t }) {
  const [loading, setLoading] = useState(false);
  const go = async () => { setLoading(true); try { await onConfirm(); } finally { setLoading(false); } };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-sm mx-4 animate-fade-in border border-border">
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
            <RefreshCw className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <p className="text-sm text-muted-foreground mb-6">{t("apiToken.rotateConfirm")}</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button onClick={go} loading={loading}>{t("apiToken.rotate")}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ApiTokenForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const isEditing = Boolean(id);
  const isSuperAdmin = !!user?.capabilities?.manage_system;

  const [form, setForm] = useState({ name: "", allowedIps: "", rateLimit: 60, enabled: true, canBiometric: true, company: "" });
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [rotating, setRotating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (isSuperAdmin) api.get("/api/companies/").then((r) => setCompanies(r.data)).catch(() => {});
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isEditing) return;
    api.get(`/api/api-tokens/${id}/`)
      .then((r) => {
        const d = r.data;
        setForm({ name: d.name || "", allowedIps: (d.allowed_ips || []).join(", "),
          rateLimit: d.rate_limit_per_minute ?? 60, enabled: d.enabled ?? true,
          canBiometric: d.can_biometric ?? true, company: d.company || "" });
        setToken(d.token || "");
      })
      .catch(() => setError(t("apiToken.loadError")))
      .finally(() => setFetching(false));
  }, [id, isEditing, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(t("apiToken.nameRequired")); return; }
    setError("");
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        allowed_ips: form.allowedIps.split(",").map((s) => s.trim()).filter(Boolean),
        rate_limit_per_minute: parseInt(form.rateLimit, 10) || 0,
        enabled: form.enabled,
        can_biometric: form.canBiometric,
      };
      if (isSuperAdmin) payload.company = form.company;
      if (isEditing) {
        await api.put(`/api/api-tokens/${id}/`, payload);
        navigate(LIST_PATH);
      } else {
        const res = await api.post("/api/api-tokens/", payload);
        // Reveal the token on creation and stay on the (now-edit) form to copy it.
        setToken(res.data.token || "");
        setShowToken(true);
        navigate(`/liveness/admin/api-tokens/${res.data.id}/edit`, { replace: true });
      }
    } catch (err) {
      const d = err.response?.data;
      setError(d && typeof d === "object" ? Object.values(d).flat().join(" ") : t("apiToken.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const rotate = async () => {
    const res = await api.post(`/api/api-tokens/${id}/rotate-token/`);
    setToken(res.data.token);
    setShowToken(true);
    setRotating(false);
  };

  const copyToken = () => { if (token) navigator.clipboard.writeText(token).catch(() => {}); };

  return (
    <div className="animate-fade-in">
      <button onClick={() => navigate(LIST_PATH)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" />{t("common.back")}
      </button>
      <h1 className="text-2xl font-bold tracking-tight mb-1">{isEditing ? t("apiToken.edit") : t("apiToken.new")}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t("apiToken.subtitle")}</p>

      <Card className="w-full">
        <CardContent className="pt-6">
          {fetching ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="tk-company">{t("common.company")}</Label>
                  <select id="tk-company" value={form.company} onChange={(e) => set("company", e.target.value)} required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    <option value="">{t("common.selectCompany")}</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="tk-name">{t("common.name")} <span className="text-red-500">*</span></Label>
                <Input id="tk-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("apiToken.namePlaceholder")} required autoFocus />
              </div>

              {isEditing && (
                <div className="space-y-2">
                  <Label>{t("apiToken.token")}</Label>
                  <div className="flex items-center gap-2">
                    <Input type={showToken ? "text" : "password"} value={token} readOnly className="font-mono text-xs" />
                    <button type="button" onClick={() => setShowToken(!showToken)} className="p-2 text-muted-foreground hover:text-foreground" title={showToken ? t("apiToken.hide") : t("apiToken.show")}>
                      {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button type="button" onClick={copyToken} className="p-2 text-muted-foreground hover:text-foreground" title={t("common.copy")}>
                      <Copy className="h-4 w-4" />
                    </button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setRotating(true)}>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" />{t("apiToken.rotate")}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("apiToken.tokenHint")}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="tk-ips">{t("apiToken.ipAcl")}</Label>
                <Input id="tk-ips" value={form.allowedIps} onChange={(e) => set("allowedIps", e.target.value)} placeholder="203.0.113.10, 10.0.0.0/24" />
                <p className="text-xs text-muted-foreground">{t("apiToken.ipAclHint")}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tk-rate">{t("apiToken.rateLimit")}</Label>
                <Input id="tk-rate" type="number" min={0} value={form.rateLimit} onChange={(e) => set("rateLimit", e.target.value)} />
                <p className="text-xs text-muted-foreground">{t("apiToken.rateLimitHint")}</p>
              </div>

              <div className="space-y-2">
                <Label>{t("apiToken.permissions")}</Label>
                <p className="text-xs text-muted-foreground">{t("apiToken.permissionsHint")}</p>
                <div className="flex items-center gap-3 text-sm">
                  <Switch checked={form.canBiometric} onChange={(v) => set("canBiometric", v)} ariaLabel={t("apiTokenForm.biometricScopeAria")} />
                  <span>{t("apiTokenForm.biometricScope")}</span>
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <Switch checked={form.enabled} onChange={(v) => set("enabled", v)} ariaLabel={t("apiToken.enabled")} />
                <span>{t("apiToken.enabled")}</span>
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <div className="flex gap-3 pt-2">
                <Button type="submit" loading={loading}>{isEditing ? t("common.save") : t("apiToken.create")}</Button>
                <Button type="button" variant="outline" onClick={() => navigate(LIST_PATH)}>{t("common.cancel")}</Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {rotating && <RotateConfirmModal t={t} onClose={() => setRotating(false)} onConfirm={rotate} />}
    </div>
  );
}
